import React, { Suspense } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { AuthProvider } from './context/AuthContext';
import { HeaderProvider } from './context/HeaderContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Header from './components/Header';
import { useTheme } from '@mirray/core/hooks/useTheme';
import { lazyWithRetry } from './utils/lazyWithRetry';

// Lazy load pages for better performance (retry on stale chunk after deploy)
const HomePage = lazyWithRetry(() => import('./pages/index'));
const AboutPage = lazyWithRetry(() => import('./pages/AboutPage'));
const FramePage = lazyWithRetry(() => import('./pages/frame'));
const FramesPage = lazyWithRetry(() => import('./pages/frames'));
const LoginPage = lazyWithRetry(() => import('./pages/login'));
const SignupPage = lazyWithRetry(() => import('./pages/signup'));
const SignupWithInvitePage = lazyWithRetry(() => import('./pages/signup-with-invite'));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/reset-password'));
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/forgot-password'));
const ProfilePage = lazyWithRetry(() => import('./pages/profile'));
const PrivacyPage = lazyWithRetry(() => import('./pages/PrivacyPage'));
const TermsPage = lazyWithRetry(() => import('./pages/TermsPage'));
const DashboardPage = lazyWithRetry(() => import('./pages/dashboard'));
const CatalogPage = lazyWithRetry(() => import('./pages/catalog'));
const StorylinePage = lazyWithRetry(() => import('./pages/storyline'));
// ChapterPage removed - using flattened hierarchy
const ScenePage = lazyWithRetry(() => import('./pages/scene'));
const TestActorsPage = lazyWithRetry(() => import('./pages/test-actors'));
const SharedGraphPage = lazyWithRetry(() => import('./pages/shared-graph'));
const InsightsPage = lazyWithRetry(() => import('./pages/insights'));
const ActorPage = lazyWithRetry(() => import('./pages/actor'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-snow-white">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-accent mx-auto mb-4"></div>
      <p className="text-fog-gray">Loading...</p>
    </div>
  </div>
);

// Error fallback component
const ErrorFallback = ({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) => (
  <div className="min-h-screen flex items-center justify-center bg-snow-white px-4">
    <div className="max-w-md w-full text-center">
      <div className="mb-6">
        <svg className="w-16 h-16 text-error-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <h1 className="text-2xl font-bold text-graphite-lines mb-2">Something went wrong</h1>
        <p className="text-fog-gray mb-6">
          {error.message || 'An unexpected error occurred. Please try refreshing the page.'}
        </p>
      </div>
      
      <div className="space-y-3">
        <button
          onClick={resetErrorBoundary}
          className="w-full px-4 py-2 bg-indigo-accent text-snow-white rounded-lg hover:bg-indigo-accent/90 transition-colors"
        >
          Try again
        </button>
        <button
          onClick={() => window.location.href = '/'}
          className="w-full px-4 py-2 border border-graphite-lines/20 text-graphite-lines rounded-lg hover:bg-fog-gray/10 transition-colors"
        >
          Go to homepage
        </button>
      </div>
    </div>
  </div>
);

// 404 component
const NotFound = () => (
  <div className="min-h-screen flex items-center justify-center bg-snow-white px-4">
    <div className="max-w-md w-full text-center">
      <div className="mb-6">
        <h1 className="text-6xl font-bold text-fog-gray mb-4">404</h1>
        <h2 className="text-2xl font-bold text-graphite-lines mb-2">Page not found</h2>
        <p className="text-fog-gray mb-6">
          The page you're looking for doesn't exist or has been moved.
        </p>
      </div>
      
      <a
        href="/"
        className="inline-block px-6 py-3 bg-indigo-accent text-snow-white rounded-lg hover:bg-indigo-accent/90 transition-colors"
      >
        Go to homepage
      </a>
    </div>
  </div>
);

// Theme Provider component
const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useTheme(); // sets data-theme on <html> so CSS variable overrides apply everywhere
  return <>{children}</>;
};

// Layout component
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-snow-white">
    <Header />
    <main>
      <Suspense fallback={<LoadingSpinner />}>
        {children}
      </Suspense>
    </main>
  </div>
);

// Create router with future flags
const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout><HomePage /></Layout>,
  },
  {
    path: "/about",
    element: <Layout><AboutPage /></Layout>,
  },
  {
    path: "/reveal/:session_id",
    element: <ProtectedRoute><Layout><FramePage /></Layout></ProtectedRoute>,
  },
  {
    path: "/reveal",
    element: <ProtectedRoute><Layout><FramePage /></Layout></ProtectedRoute>,
  },
  {
    path: "/clarity",
    element: <ProtectedRoute><Layout><FramePage /></Layout></ProtectedRoute>,
  },
  {
    path: "/clarity/:session_id",
    element: <ProtectedRoute><Layout><FramePage /></Layout></ProtectedRoute>,
  },
  {
    path: "/catalog",
    element: <ProtectedRoute><Layout><CatalogPage /></Layout></ProtectedRoute>,
  },
  {
    path: "/frames",
    element: <ProtectedRoute><Layout><FramesPage /></Layout></ProtectedRoute>,
  },
  {
    path: "/group",
    element: <Layout><div className="container mx-auto px-4 py-12"><h1 className="text-3xl font-bold text-graphite-lines font-editorial">Group Storyline Page</h1><p className="mt-4 text-fog-gray">Coming soon...</p></div></Layout>,
  },
  {
    path: "/login",
    element: <Layout><LoginPage /></Layout>,
  },
  {
    path: "/signup",
    element: <Layout><SignupPage /></Layout>,
  },
  {
    path: "/signup-with-invite",
    element: <Layout><SignupWithInvitePage /></Layout>,
  },
  {
    path: "/shared/:token",
    element: <Layout><SharedGraphPage /></Layout>,
  },
  {
    path: "/reset-password",
    element: <Layout><ResetPasswordPage /></Layout>,
  },
  {
    path: "/forgot-password",
    element: <Layout><ForgotPasswordPage /></Layout>,
  },
  {
    path: "/dashboard",
    element: <ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>,
  },
  {
    path: "/storylines",
    element: <ProtectedRoute><Layout><CatalogPage /></Layout></ProtectedRoute>,
  },
  {
    path: "/storylines/:storylineId",
    element: <ProtectedRoute><Layout><StorylinePage /></Layout></ProtectedRoute>,
  },
  {
    path: "/storyline/:storylineId",
    element: <ProtectedRoute><Layout><StorylinePage /></Layout></ProtectedRoute>,
  },
  // Chapter routes removed - using flattened hierarchy: Storyline → Scene
  {
    path: "/scenes/:sceneId",
    element: <ProtectedRoute><Layout><ScenePage /></Layout></ProtectedRoute>,
  },
  {
    path: "/scene/:sceneId",
    element: <ProtectedRoute><Layout><ScenePage /></Layout></ProtectedRoute>,
  },
  {
    path: "/profile",
    element: <ProtectedRoute><Layout><ProfilePage /></Layout></ProtectedRoute>,
  },
  {
    path: "/test-actors",
    element: <ProtectedRoute><Layout><TestActorsPage /></Layout></ProtectedRoute>,
  },
  {
    path: "/insights",
    element: <ProtectedRoute><Layout><InsightsPage /></Layout></ProtectedRoute>,
  },
  {
    path: "/actor/:id",
    element: <ProtectedRoute><Layout><ActorPage /></Layout></ProtectedRoute>,
  },
  {
    path: "/terms",
    element: <Layout><TermsPage /></Layout>,
  },
  {
    path: "/privacy",
    element: <Layout><PrivacyPage /></Layout>,
  },
  {
    path: "*",
    element: <Layout><NotFound /></Layout>,
  },
], {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true,
  } as any,
});

const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ThemeProvider>
        <AuthProvider queryClient={queryClient}>
          <HeaderProvider>
            <RouterProvider router={router} />
          </HeaderProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </QueryClientProvider>
);

export default App;
